<template>
  <div class="st-page" v-loading="loading">

    <div class="st-header">
      <div class="st-inner">
        <h1 class="st-title">Settings</h1>
        <p class="st-subtitle">Manage your account and business preferences</p>
      </div>
      <div class="st-header-rule"></div>
    </div>

    <div class="st-inner st-body">
      <SettingsSidebar />

      <div class="st-content">

        <!-- Content header -->
        <div class="st-content-head">
          <div>
            <div class="st-content-title">Team Members</div>
            <div class="st-content-sub">{{ users.length }} member{{ users.length !== 1 ? 's' : '' }} in your account</div>
          </div>
          <div class="st-head-actions">
            <div class="st-search-wrap">
              <svg class="st-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input v-model="search" class="st-search" placeholder="Search members..." />
            </div>
            <button v-if="isAdmin" class="st-btn" @click="inviteUsers">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Invite User
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="st-table">
          <div class="st-thead">
            <div class="st-th">Member</div>
            <div class="st-th">Phone</div>
            <div class="st-th">Email</div>
            <div class="st-th">Role</div>
            <div class="st-th st-th--end"></div>
          </div>

          <div class="st-tbody">
            <div v-for="(row, i) in filterTableData" :key="i" class="st-row-wrap">
              <div class="st-row">
                <div class="st-td">
                  <div class="st-user-cell">
                    <div class="st-avatar" :class="{ 'st-avatar--off': row.status === 'inactive' }">{{ initials(row.user_name) }}</div>
                    <span class="st-user-name" :class="{ 'st-user-name--off': row.status === 'inactive' }">{{ row.user_name }}</span>
                    <span v-if="row.status === 'inactive'" class="st-deactivated">Deactivated</span>
                  </div>
                </div>
                <div class="st-td"><span class="st-muted">{{ row.user_phone }}</span></div>
                <div class="st-td"><span class="st-muted">{{ row.user_email }}</span></div>
                <div class="st-td">
                  <span class="st-role" :class="row.user_type === 'Admin' ? 'st-role--admin' : 'st-role--user'">
                    {{ row.user_type }}
                  </span>
                </div>
                <div class="st-td st-td--end">
                  <span v-if="isAdmin" class="st-action-link st-action-link--edit" @click="router.push('/edituser/' + row.id)">
                    Edit
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </span>
                  <span v-if="isAdmin && row.id !== store.user?.id"
                        class="st-action-link"
                        :class="(row.status ?? 'active') === 'active' ? 'st-action-link--deactivate' : 'st-action-link--activate'"
                        @click="toggleStatus(row)">
                    {{ (row.status ?? 'active') === 'active' ? 'Deactivate' : 'Activate' }}
                    <svg v-if="(row.status ?? 'active') === 'active'" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                    <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11.5 14.5 16 9.5"/></svg>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="st-empty" v-if="!loading && filterTableData.length === 0">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccdeff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <div class="st-empty-text">{{ search ? 'No members match your search' : 'No team members yet' }}</div>
            <button v-if="isAdmin" class="st-btn" @click="inviteUsers">Invite your first member</button>
          </div>
        </div>

      </div>
    </div>
  </div>

  <!-- Deactivate / Activate confirm -->
  <Teleport to="body">
    <div v-if="confirmModal.open" class="st-confirm-overlay" @click.self="confirmModal.open = false">
      <div class="st-confirm-card">
        <div class="st-confirm-icon" :class="confirmModal.isActive ? 'st-confirm-icon--warn' : 'st-confirm-icon--ok'">
          <svg v-if="confirmModal.isActive" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
          <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="8.5 12 11 14.5 16 9"/></svg>
        </div>
        <div class="st-confirm-title">{{ confirmModal.isActive ? 'Deactivate user?' : 'Activate user?' }}</div>
        <div class="st-confirm-msg">
          {{ confirmModal.isActive
            ? `${confirmModal.row?.user_name} won't be able to sign in until you reactivate them.`
            : `${confirmModal.row?.user_name} will be able to sign in again.` }}
        </div>
        <div class="st-confirm-actions">
          <button class="st-confirm-btn st-confirm-btn--ghost" @click="confirmModal.open = false" :disabled="confirmModal.saving">Cancel</button>
          <button class="st-confirm-btn" :class="confirmModal.isActive ? 'st-confirm-btn--danger' : 'st-confirm-btn--primary'" @click="doToggle" :disabled="confirmModal.saving">
            {{ confirmModal.saving ? 'Saving…' : (confirmModal.isActive ? 'Deactivate' : 'Activate') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { message as ElMessage } from '../../utils/message';
import api from '../../api';
import SettingsSidebar from '../settings/SettingsSidebar.vue';
import { useAuthStore } from '../../store/authStore';

const store   = useAuthStore();
const isAdmin = computed(() => store.isAdmin);

const router  = useRouter();
const users   = ref<any[]>([]);
const loading = ref(true);
const search  = ref('');

// Two-letter avatar initials: first letters of the first + last word, or the
// first two characters of a single-word name.
const initials = (name: string) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const fetchUsers = async () => {
  try {
    const res   = await api.get('customer/users');
    users.value = (res.data.data.users ?? []).map((u: any) => ({
      ...u,
      user_name:  u.name,
      user_email: u.email,
      user_phone: u.phone || '—',
      user_type:  u.role === 'admin' ? 'Admin' : 'User',
    }));
  } catch {
    ElMessage.error('Failed to load users.');
  } finally {
    loading.value = false;
  }
};

const filterTableData = computed(() => {
  const q = search.value.toLowerCase().trim();
  if (!q) return users.value;
  return users.value.filter(u =>
    u.user_name?.toLowerCase().includes(q) ||
    u.user_email?.toLowerCase().includes(q)
  );
});

const inviteUsers = () => router.push('/add_user');

// Custom confirm modal (the portal doesn't fully set up Element Plus, so
// ElMessageBox.confirm never rendered — which is why the old action did nothing).
const confirmModal = reactive({ open: false, row: null as any, isActive: true, saving: false });

const toggleStatus = (row: any) => {
  confirmModal.row = row;
  confirmModal.isActive = (row.status ?? 'active') === 'active';
  confirmModal.open = true;
};

const doToggle = async () => {
  const row = confirmModal.row;
  if (!row || confirmModal.saving) return;
  confirmModal.saving = true;
  try {
    await api.patch(`customer/users/${row.id}`, { status: confirmModal.isActive ? 'inactive' : 'active' });
    ElMessage.success(confirmModal.isActive ? 'User deactivated' : 'User activated');
    confirmModal.open = false;
    fetchUsers();
  } catch (e) {
    ElMessage.error('Could not update the user. Please try again.');
  } finally {
    confirmModal.saving = false;
  }
};

onMounted(fetchUsers);
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }

.st-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.st-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

.st-header { background: white; padding: 24px 0 0; }
.st-header .st-inner { padding-bottom: 18px; }
.st-title { font-size: 1.4rem; font-weight: 700; color: #0f1729; margin: 0 0 3px; }
.st-subtitle { font-size: 0.83rem; color: #94a3b8; margin: 0; }
.st-header-rule { height: 1.5px; background: #e8f0ff; }

.st-body { display: flex; gap: 24px; padding-top: 28px; padding-bottom: 40px; align-items: flex-start; }
.st-content { flex: 1; min-width: 0; }

/* Content header */
.st-content-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 16px;
}
.st-content-title { font-size: 1rem; font-weight: 700; color: #0f1729; margin-bottom: 2px; }
.st-content-sub { font-size: 0.78rem; color: #94a3b8; }
.st-head-actions { display: flex; gap: 8px; align-items: center; }

.st-search-wrap { position: relative; display: flex; align-items: center; }
.st-search-icon { position: absolute; left: 10px; color: #94a3b8; pointer-events: none; }
.st-search {
  height: 38px; padding: 0 12px 0 32px; border-radius: 8px;
  border: 1.5px solid #e2e8f0; font-family: 'DM Sans', sans-serif;
  font-size: 0.82rem; color: #334155; outline: none; width: 200px;
  transition: border-color 0.15s;
}
.st-search:focus { border-color: #0061eb; }
.st-search::placeholder { color: #94a3b8; }

.st-btn {
  height: 38px; padding: 0 16px;
  background: #0061eb; color: white; border: none; border-radius: 8px;
  font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  transition: background 0.15s; white-space: nowrap;
}
.st-btn:hover { background: #0050c7; }

/* Table */
.st-table { background: white; border: 1.5px solid #ccdeff; border-radius: 12px; overflow: hidden; }
.st-thead {
  display: grid; grid-template-columns: 1.2fr 160px 1fr 110px 180px;
  background: #f0f6ff; border-bottom: 1.5px solid #ccdeff; padding: 0 20px;
}
.st-th {
  font-size: 0.68rem; font-weight: 700; color: #6b86b8;
  text-transform: uppercase; letter-spacing: 0.08em; padding: 12px 10px;
}
.st-th--end { text-align: right; }

.st-tbody { display: flex; flex-direction: column; }
.st-row-wrap { border-bottom: 1px solid #e8f0ff; }
.st-row-wrap:last-child { border-bottom: none; }
.st-row {
  display: grid; grid-template-columns: 1.2fr 160px 1fr 110px 180px;
  align-items: center; padding: 0 20px; height: 56px; transition: background 0.12s;
}
.st-row:hover { background: #f0f6ff; }
.st-td { padding: 0 10px; overflow: hidden; }
.st-td--end { display: flex; justify-content: flex-end; align-items: center; gap: 16px; }

.st-user-cell { display: flex; align-items: center; gap: 10px; }
.st-avatar {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  background: #f0f6ff; border: 1px solid #ccdeff;
  color: #0061eb; display: flex; align-items: center; justify-content: center;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.02em;
}
.st-user-name { font-size: 0.84rem; font-weight: 600; color: #0f1729; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.st-muted { font-size: 0.81rem; color: #64748b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; }

.st-role {
  display: inline-flex; align-items: center; padding: 3px 10px;
  border-radius: 20px; font-size: 0.72rem; font-weight: 600; white-space: nowrap; border: 1.5px solid;
}
.st-role--admin { color: #6d28d9; background: #f5f3ff; border-color: #ddd6fe; }
.st-role--user  { color: #2563eb; background: #eff6ff; border-color: #93c5fd; }

.st-action-link {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.78rem; font-weight: 600; color: #ef4444;
  cursor: pointer; transition: gap 0.15s, opacity 0.15s; white-space: nowrap; opacity: 0.7;
}
.st-action-link--edit { color: #0061eb; }
.st-action-link--deactivate { color: #d97706; }
.st-action-link--activate { color: #16a34a; }
.st-avatar--off { background: #f3f4f6; border-color: #e5e7eb; color: #9ca3af; }
.st-user-name--off { color: #9ca3af; }
.st-deactivated {
  font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
  color: #9ca3af; background: #f3f4f6; border-radius: 4px; padding: 2px 7px; white-space: nowrap;
}

/* Deactivate / Activate confirm modal */
.st-confirm-overlay {
  position: fixed; inset: 0; z-index: 3000;
  background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center;
  font-family: 'DM Sans', sans-serif; padding: 16px;
}
.st-confirm-card {
  background: #fff; border-radius: 16px; padding: 26px 24px 20px;
  width: 380px; max-width: 100%; text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
}
.st-confirm-icon {
  width: 48px; height: 48px; border-radius: 50%; margin: 0 auto 14px;
  display: flex; align-items: center; justify-content: center;
}
.st-confirm-icon--warn { background: #fef3c7; color: #d97706; }
.st-confirm-icon--ok { background: #dcfce7; color: #16a34a; }
.st-confirm-title { font-size: 1.05rem; font-weight: 700; color: #0f1729; margin-bottom: 6px; }
.st-confirm-msg { font-size: 0.85rem; color: #64748b; line-height: 1.45; margin-bottom: 20px; }
.st-confirm-actions { display: flex; gap: 10px; }
.st-confirm-btn {
  flex: 1; padding: 10px; border-radius: 10px; border: none;
  font-family: 'DM Sans', sans-serif; font-size: 0.85rem; font-weight: 700;
  cursor: pointer; transition: background 0.15s, opacity 0.15s;
}
.st-confirm-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.st-confirm-btn--ghost { background: #f1f5f9; color: #475569; }
.st-confirm-btn--ghost:hover { background: #e2e8f0; }
.st-confirm-btn--primary { background: #0061eb; color: #fff; }
.st-confirm-btn--primary:hover { background: #0050c7; }
.st-confirm-btn--danger { background: #dc2626; color: #fff; }
.st-confirm-btn--danger:hover { background: #b91c1c; }
.st-row:hover .st-action-link { gap: 7px; opacity: 1; }

.st-empty {
  text-align: center; padding: 56px 0;
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.st-empty-text { font-size: 0.9rem; font-weight: 500; color: #94a3b8; }
</style>
